import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';

import { MaskitoElementPredicate, MaskitoOptions } from '@maskito/core';
import { select, Store } from '@ngrx/store';

import { isNull } from 'lodash-es';
import { Observable } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

import { PrimarySubscriptionPlanAccessAliasEnum } from '../../enums/monetization';
import { MonetizationService } from '../../misc/services/monetization.service';
import * as fromPropertyProfileActions from '../../store/actions/property-profile.actions';
import * as fromSettingActions from '../../store/actions/setting.actions';
import * as fromSettingSelectors from '../../store/selectors/setting.selectors';
import { SearchableSelectOption } from '../../typings/searchable-select';

interface OwnerFormGroup {
  county: FormControl<string | null>;
  firstName: FormControl<string | null>;
  lastName: FormControl<string | null>;
  state: FormControl<string | null>;
  zip: FormControl<string | null>;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'red-property-search-owner',
  standalone: false,
  templateUrl: './owner.page.html'
})
export class OwnerPageComponent {
  private monetizationService = inject(MonetizationService);
  private store = inject(Store);

  availableStates$: Observable<SearchableSelectOption[]>;
  availableCounties$: Observable<SearchableSelectOption[]>;

  readonly zipMask: MaskitoOptions = {
    mask: [/\d/, /\d/, /\d/, /\d/, /\d/]
  };

  readonly maskPredicate: MaskitoElementPredicate = async (el) =>
    (el as unknown as HTMLIonInputElement).getInputElement();

  form: FormGroup<OwnerFormGroup> = new FormGroup<OwnerFormGroup>({
    county: new FormControl({ disabled: true, value: null }, Validators.required),
    firstName: new FormControl(null),
    lastName: new FormControl(null, Validators.required),
    state: new FormControl(null, Validators.required),
    zip: new FormControl(null)
  });

  constructor() {
    this.availableStates$ = this.store.pipe(select(fromSettingSelectors.selectAvailableStates));
    this.availableCounties$ = this.store.pipe(select(fromSettingSelectors.selectAvailableCounties));

    this.store
      .pipe(select(fromSettingSelectors.selectSelectedState), distinctUntilChanged())
      .subscribe((selectedState) => {
        if (this.form.controls.state.value !== selectedState) {
          this.form.controls.state.setValue(selectedState, { emitEvent: false });
          if (isNull(selectedState)) {
            this.form.controls.county.disable();
          } else {
            this.form.controls.county.enable();
          }
        }
      });

    this.store
      .pipe(select(fromSettingSelectors.selectSelectedCounty), distinctUntilChanged())
      .subscribe((selectedCounty) => {
        if (this.form.controls.county.value !== selectedCounty) {
          this.form.controls.county.setValue(selectedCounty, { emitEvent: false });
        }
      });

    this.form.controls.state.valueChanges.pipe(distinctUntilChanged()).subscribe((value) => {
      this.store.dispatch(fromSettingActions.updateSelectedState({ payload: { state: value } }));
      this.form.controls.county.markAsUntouched();
      if (isNull(value)) {
        this.form.controls.county.disable();
      } else {
        this.form.controls.county.enable();
      }
    });

    this.form.controls.county.valueChanges.pipe(distinctUntilChanged()).subscribe((value) => {
      this.store.dispatch(fromSettingActions.updateSelectedCounty({ payload: { county: value } }));
    });
  }

  async onSubmit() {
    const hasAccess = await this.monetizationService.accessFeature([PrimarySubscriptionPlanAccessAliasEnum.A]);
    if (hasAccess) {
      const formValues = this.form.getRawValue();
      this.store.dispatch(
        fromPropertyProfileActions.searchByOwner({
          payload: {
            county: formValues.county || '',
            firstName: formValues.firstName || '',
            lastName: formValues.lastName || '',
            state: formValues.state || '',
            zip: formValues.zip || ''
          }
        })
      );
    }
  }
}
